/**
 * Modèle "galerie souvenirs" partagé entre l'admin et le site (dupliqué
 * depuis demo-admin/lib/galleryShape.ts — repos séparés, pas de package
 * commun pour l'instant).
 */

import type { SiteSliderImage } from './siteSliderShape'

export const HIGHLIGHTS_SECTION_ID = '__highlights__'

export type GalleryMediaType = 'image' | 'video'

export interface GallerySection {
  id: string
  title: string
  order: number
}

export interface GalleryItem {
  id: string
  url: string
  type: GalleryMediaType
  sectionId: string | null
  order: number
  alt?: string
  caption?: string
  poster?: string
  /* Variantes optimisées générées par le pipeline Sharp à l'upload.
     Optionnelles : items uploadés avant Phase 1 n'en ont pas (fallback url). */
  thumbnail?: string  // WebP 600px ~30 KB, pour la grille masonry
  preview?: string    // WebP 2048px ~200 KB, pour la lightbox
  blurhash?: string   // 30 bytes base83, placeholder pendant le chargement
  width?: number      // dims originales pour aspect ratio masonry
  height?: number
  ratio?: number      // largeur/hauteur ; alternative directe à width/height pour le masonry
  pos?: string        // point focal object-position ("X% Y%") pour la grille masonry (défaut centre)
}

export interface EventGalleryShape {
  sections: GallerySection[]
  items: GalleryItem[]
}

export function inferMediaType(url: string): GalleryMediaType {
  return /\.(mp4|webm|mov|m4v|avi)(\?|$)/i.test(url) ? 'video' : 'image'
}

function freshId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4)
}

/** Normalise raw (null, array plat ancien, objet nouveau) → EventGalleryShape. */
export function normalizeGallery(raw: unknown): EventGalleryShape {
  if (raw == null) return { sections: [], items: [] }
  if (Array.isArray(raw)) {
    const items: GalleryItem[] = raw
      .filter((it): it is Record<string, unknown> => typeof it === 'object' && it !== null)
      .map((it, i) => {
        const url = String(it.url ?? '')
        const type: GalleryMediaType =
          it.type === 'image' || it.type === 'video' ? it.type : inferMediaType(url)
        return {
          id: typeof it.id === 'string' ? it.id : freshId(),
          url,
          type,
          sectionId: null,
          order: typeof it.order === 'number' ? it.order : i,
          alt: typeof it.alt === 'string' ? it.alt : undefined,
          caption: typeof it.caption === 'string' ? it.caption : undefined,
          poster: typeof it.poster === 'string' ? it.poster : undefined,
          thumbnail: typeof it.thumbnail === 'string' ? it.thumbnail : undefined,
          preview: typeof it.preview === 'string' ? it.preview : undefined,
          blurhash: typeof it.blurhash === 'string' ? it.blurhash : undefined,
          width: typeof it.width === 'number' ? it.width : undefined,
          height: typeof it.height === 'number' ? it.height : undefined,
          pos: typeof it.pos === 'string' ? it.pos : undefined,
        }
      })
    return { sections: [], items }
  }
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    const rawSections = Array.isArray(obj.sections) ? obj.sections : []
    const rawItems = Array.isArray(obj.items) ? obj.items : []
    const sections: GallerySection[] = rawSections
      .filter((s): s is Record<string, unknown> => typeof s === 'object' && s !== null)
      .map((s, i) => ({
        id: typeof s.id === 'string' ? s.id : freshId(),
        title: typeof s.title === 'string' ? s.title : 'Section',
        order: typeof s.order === 'number' ? s.order : i,
      }))
    const sectionIds = new Set(sections.map(s => s.id))
    const items: GalleryItem[] = rawItems
      .filter((it): it is Record<string, unknown> => typeof it === 'object' && it !== null)
      .map((it, i) => {
        const url = String(it.url ?? '')
        const type: GalleryMediaType =
          it.type === 'image' || it.type === 'video' ? it.type : inferMediaType(url)
        const sectionId =
          typeof it.sectionId === 'string' && sectionIds.has(it.sectionId)
            ? it.sectionId
            : null
        return {
          id: typeof it.id === 'string' ? it.id : freshId(),
          url,
          type,
          sectionId,
          order: typeof it.order === 'number' ? it.order : i,
          alt: typeof it.alt === 'string' ? it.alt : undefined,
          caption: typeof it.caption === 'string' ? it.caption : undefined,
          poster: typeof it.poster === 'string' ? it.poster : undefined,
          thumbnail: typeof it.thumbnail === 'string' ? it.thumbnail : undefined,
          preview: typeof it.preview === 'string' ? it.preview : undefined,
          blurhash: typeof it.blurhash === 'string' ? it.blurhash : undefined,
          width: typeof it.width === 'number' ? it.width : undefined,
          height: typeof it.height === 'number' ? it.height : undefined,
          pos: typeof it.pos === 'string' ? it.pos : undefined,
        }
      })
    return { sections, items }
  }
  return { sections: [], items: [] }
}

/** Transforme un shape "plat possible" en structure ordonnée pour le site :
 *  un tableau de "pseudo-sections" prêtes à consommer.
 *  - Si la galerie a des sections, on les utilise (triées par order)
 *  - Sinon, on wrappe tous les items dans une section virtuelle "Souvenirs"
 *  - Les items sans sectionId rejoignent une section "Autres" en fin si
 *    présence simultanée de sections classées
 */
export interface ResolvedSection {
  id: string
  title: string
  items: GalleryItem[]
}

/** Convertit les SiteSliderImage du carrousel vedette en GalleryItem
 *  pour les afficher comme une section "Highlights" dans le modal. */
function highlightsToGalleryItems(highlights: SiteSliderImage[]): GalleryItem[] {
  return highlights.map((h, i) => ({
    id: `highlight-${h.id}`,
    url: h.url,
    type: 'image' as const,
    sectionId: HIGHLIGHTS_SECTION_ID,
    order: i,
    alt: h.title ?? `Highlight ${i + 1}`,
    caption: h.subtitle ?? h.title,
  }))
}

export function resolveSectionsForDisplay(
  shape: EventGalleryShape,
  highlights?: SiteSliderImage[],
): ResolvedSection[] {
  const { sections, items } = shape

  /* Section virtuelle "Highlights" préfixée en tête si le carrousel
     vedette contient des images. Indépendante de la galerie Stories. */
  const highlightsSection: ResolvedSection | null =
    highlights && highlights.length > 0
      ? {
          id: HIGHLIGHTS_SECTION_ID,
          title: 'Highlights',
          items: highlightsToGalleryItems(highlights),
        }
      : null

  if (items.length === 0) {
    return highlightsSection ? [highlightsSection] : []
  }

  // Cas simple : pas de sections du tout → tout dans "Souvenirs"
  if (sections.length === 0) {
    const sorted = [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    const main: ResolvedSection = { id: 'all', title: 'Souvenirs', items: sorted }
    return highlightsSection ? [highlightsSection, main] : [main]
  }

  const ordered = [...sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const byId = new Map<string, GalleryItem[]>()
  const unsorted: GalleryItem[] = []
  for (const it of items) {
    if (it.sectionId && ordered.some(s => s.id === it.sectionId)) {
      const arr = byId.get(it.sectionId) ?? []
      arr.push(it)
      byId.set(it.sectionId, arr)
    } else {
      unsorted.push(it)
    }
  }

  const resolved: ResolvedSection[] = ordered
    .map(s => ({
      id: s.id,
      title: s.title,
      items: (byId.get(s.id) ?? []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }))
    .filter(s => s.items.length > 0)

  if (unsorted.length > 0) {
    resolved.push({
      id: '__unsorted__',
      title: 'Autres',
      items: unsorted.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    })
  }

  return highlightsSection ? [highlightsSection, ...resolved] : resolved
}
